// src/components/leitner/PdfExportModal.jsx
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { 
  X, Download, FileText, Loader2, Server, GripVertical, ArrowUp, ArrowDown,
  CheckSquare, Square, ChevronDown, ChevronUp, Search, Award, 
  Image, Upload, Columns, AlignLeft, Type, User, School, Stamp, Droplets
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { MathRenderer } from '../math/MathRenderer';
import CategoryFilter from './CategoryFilter';

const PdfExportModal = ({ 
  isOpen, 
  onClose, 
  questions, 
  title: defaultTitle = 'Question Bank',
  bookData = null // Pass book data for logo
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [showAnswers, setShowAnswers] = useState(true);
  const [status, setStatus] = useState('');
  const [error, setError] = useState(null);
  
  // PDF Settings
  const [pdfTitle, setPdfTitle] = useState(defaultTitle);
  const [learnerName, setLearnerName] = useState('');
  const [learnerClass, setLearnerClass] = useState('');
  const [layout, setLayout] = useState('portrait'); // portrait | landscape
  const [signature, setSignature] = useState('');
  const [watermark, setWatermark] = useState('');
  const [logoSource, setLogoSource] = useState('book'); // book | custom | none
  const [customLogo, setCustomLogo] = useState(null);
  const [customLogoPreview, setCustomLogoPreview] = useState(null);
  const logoInputRef = useRef(null);
  
  // Filter state
  const [filters, setFilters] = useState({ 
    subject: null, 
    grades: [],
    topics: [] 
  });
  const [searchQuery, setSearchQuery] = useState('');
  
  // Selection states
  const [selectedQuestionIds, setSelectedQuestionIds] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [showPdfSettings, setShowPdfSettings] = useState(true);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  const [orderedSelectedIds, setOrderedSelectedIds] = useState([]);

  const [showResponseSection, setShowResponseSection] = useState(true);

  // Reset title when modal opens
  useEffect(() => {
    if (isOpen) {
      setPdfTitle(defaultTitle);
    }
  }, [isOpen, defaultTitle]);

  // Extract unique filter options
  const categories = useMemo(() => {
    const subjects = new Set();
    const grades = new Set();
    const topics = new Set();
    
    questions.forEach(q => {
      if (q.subject) subjects.add(q.subject);
      if (Array.isArray(q.grades)) {
        q.grades.forEach(g => grades.add(String(g)));
      } else if (q.grade) {
        grades.add(String(q.grade));
      }
      if (q.topics?.length) q.topics.forEach(t => topics.add(t));
    });
    
    return {
      subjects: Array.from(subjects).sort(),
      grades: Array.from(grades).sort(),
      topics: Array.from(topics).sort()
    };
  }, [questions]);

  // Filter questions
  const filteredQuestions = useMemo(() => {
    return questions
      .filter(q => {
        if (filters.subject && q.subject !== filters.subject) return false;
        
        if (filters.grades && filters.grades.length > 0) {
          const questionGrades = Array.isArray(q.grades) 
            ? q.grades.map(String) 
            : (q.grade ? [String(q.grade)] : []);
          const hasMatchingGrade = filters.grades.some(g => questionGrades.includes(g));
          if (!hasMatchingGrade) return false;
        }
        
        if (filters.topics && filters.topics.length > 0) {
          const questionTopics = q.topics || [];
          const hasMatchingTopic = filters.topics.some(t => questionTopics.includes(t));
          if (!hasMatchingTopic) return false;
        }
        
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          const text = q.type === 'simple' ? q.text : q.stemText;
          if (!text?.toLowerCase().includes(query)) return false;
        }
        
        return true;
      })
      .sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || new Date(a.createdAt || 0).getTime();
        const bTime = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || new Date(b.createdAt || 0).getTime();
        return aTime - bTime; // Ascending: oldest first
      });
  }, [questions, filters, searchQuery]);

  useEffect(() => {
    const ids = filteredQuestions.map(q => q.id);
    setSelectedQuestionIds(new Set(ids));
    setOrderedSelectedIds(ids);
  }, [filteredQuestions]);

  const questionsToExport = useMemo(() => {
    // Maintain order from orderedSelectedIds
    const questionMap = new Map(filteredQuestions.map(q => [q.id, q]));
    return orderedSelectedIds
      .filter(id => selectedQuestionIds.has(id) && questionMap.has(id))
      .map(id => questionMap.get(id));
  }, [filteredQuestions, selectedQuestionIds, orderedSelectedIds]);

  const toggleQuestion = (id) => {
    setSelectedQuestionIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setOrderedSelectedIds(order => order.filter(qId => qId !== id));
      } else {
        next.add(id);
        setOrderedSelectedIds(order => [...order, id]);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedQuestionIds.size === filteredQuestions.length) {
      setSelectedQuestionIds(new Set());
      setOrderedSelectedIds([]);
    } else {
      const allIds = filteredQuestions.map(q => q.id);
      setSelectedQuestionIds(new Set(allIds));
      setOrderedSelectedIds(allIds);
    }
  };

  const toggleExpanded = (id) => {
    setExpandedQuestions(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const moveQuestion = (id, direction) => {
    setOrderedSelectedIds(prev => {
      const idx = prev.indexOf(id);
      if (idx === -1) return prev;
      const newIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next;
    });
  };

  const getOrderNumber = (id) => {
    const idx = orderedSelectedIds.indexOf(id);
    return idx !== -1 ? idx + 1 : null;
  };

  const clearFilters = () => {
    setFilters({ subject: null, grades: [], topics: [] });
    setSearchQuery('');
  };

  const hasActiveFilters = filters.subject || filters.grades.length > 0 || filters.topics.length > 0 || searchQuery;

  // Logo handling
  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }
    
    setCustomLogo(file);
    const reader = new FileReader();
    reader.onload = (e) => setCustomLogoPreview(e.target.result);
    reader.readAsDataURL(file);
    setLogoSource('custom');
  };

  const getLogoDataUrl = () => {
    if (logoSource === 'none') return null;
    if (logoSource === 'custom' && customLogoPreview) return customLogoPreview;
    if (logoSource === 'book' && bookData?.logoUrl) return bookData.logoUrl;
    return null;
  };

  const base64ToBlob = (base64, mimeType = 'application/pdf') => {
    const cleanBase64 = base64.replace(/[\s\n\r]/g, '');
    const binaryString = window.atob(cleanBase64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new Blob([bytes], { type: mimeType });
  };

  const handleExport = async () => {
    if (questionsToExport.length === 0) {
      setError('Please select at least one question to export');
      return;
    }

    setIsExporting(true);
    setError(null);
    setStatus('Connecting to server...');

    try {
      const generatePdf = httpsCallable(functions, 'generateQuestionsPdf');
      
      setStatus(`Rendering ${questionsToExport.length} questions...`);
      
      const result = await generatePdf({
        questions: questionsToExport,
        title: pdfTitle,
        showAnswers,
        showResponseSection,
        // New PDF settings
        layout,
        learnerName: learnerName.trim(),
        learnerClass: learnerClass.trim(),
        signature: signature.trim(),
        watermark: watermark.trim(),
        logoUrl: getLogoDataUrl()
      });

      if (!result.data || !result.data.pdf) {
        throw new Error('No PDF data received from server');
      }

      setStatus('Preparing download...');

      const blob = base64ToBlob(result.data.pdf);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.data.filename || `${pdfTitle.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 100);

      setStatus('Complete!');
      setTimeout(() => {
        setIsExporting(false);
        setStatus('');
      }, 1500);

    } catch (err) {
      console.error('PDF export error:', err);
      setError(err.message || 'Failed to generate PDF. Please try again.');
      setIsExporting(false);
      setStatus('');
    }
  };

  if (!isOpen) return null;

  const getQuestionPreview = (q) => {
    const text = q.type === 'simple' ? q.text : q.stemText;
    if (!text) return 'No content';
    return text.length > 120 ? text.slice(0, 120) + '...' : text;
  };

  const getQuestionGrades = (q) => {
    if (Array.isArray(q.grades) && q.grades.length > 0) {
      return q.grades.join(', ');
    }
    return q.grade || '';
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
      <div className="bg-slate-900 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col border border-slate-700">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 shrink-0">
          <div className="flex items-center gap-3">
            <FileText className="h-6 w-6 text-indigo-400" />
            <h2 className="text-lg font-bold text-slate-100">Export to PDF</h2>
          </div>
          <button
            onClick={onClose}
            disabled={isExporting}
            className="p-2 hover:bg-slate-800 rounded-lg transition disabled:opacity-50 text-slate-400 hover:text-slate-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          
          {/* PDF Settings Section */}
          <div className="border border-slate-700 rounded-lg overflow-hidden">
            <button
              onClick={() => setShowPdfSettings(!showPdfSettings)}
              className="flex items-center justify-between w-full px-4 py-3 bg-slate-800/50 hover:bg-slate-800 transition"
            >
              <span className="flex items-center gap-2 text-sm font-medium text-slate-200">
                <Type className="h-4 w-4 text-indigo-400" />
                Document Settings
              </span>
              {showPdfSettings ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
            </button>
            
            {showPdfSettings && (
              <div className="p-4 space-y-4 bg-slate-800/30">
                {/* Logo Selection */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    <Image className="h-4 w-4 inline mr-2" />
                    Header Logo
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setLogoSource('none')}
                      className={`px-3 py-2 rounded-lg text-sm transition ${
                        logoSource === 'none' 
                          ? 'bg-indigo-600 text-white' 
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                    >
                      No Logo
                    </button>
                    {bookData?.logoUrl && (
                      <button
                        onClick={() => setLogoSource('book')}
                        className={`px-3 py-2 rounded-lg text-sm transition flex items-center gap-2 ${
                          logoSource === 'book' 
                            ? 'bg-indigo-600 text-white' 
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        <img src={bookData.logoUrl} alt="" className="h-5 w-5 object-contain rounded" />
                        Book Logo
                      </button>
                    )}
                    <button
                      onClick={() => logoInputRef.current?.click()}
                      className={`px-3 py-2 rounded-lg text-sm transition flex items-center gap-2 ${
                        logoSource === 'custom' 
                          ? 'bg-indigo-600 text-white' 
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                    >
                      <Upload className="h-4 w-4" />
                      {customLogoPreview ? 'Change Logo' : 'Upload Logo'}
                    </button>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                  </div>
                  {customLogoPreview && logoSource === 'custom' && (
                    <div className="mt-2 flex items-center gap-2">
                      <img src={customLogoPreview} alt="Custom logo" className="h-10 object-contain rounded bg-white p-1" />
                      <button
                        onClick={() => {
                          setCustomLogo(null);
                          setCustomLogoPreview(null);
                          setLogoSource('none');
                        }}
                        className="text-xs text-red-400 hover:text-red-300"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Document Title
                  </label>
                  <input
                    type="text"
                    value={pdfTitle}
                    onChange={(e) => setPdfTitle(e.target.value)}
                    placeholder="Enter document title"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                {/* Learner Details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      <User className="h-4 w-4 inline mr-2" />
                      Learner Name
                    </label>
                    <input
                      type="text"
                      value={learnerName}
                      onChange={(e) => setLearnerName(e.target.value)}
                      placeholder="Leave blank for fill-in space"
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      <School className="h-4 w-4 inline mr-2" />
                      Class / Grade
                    </label>
                    <input
                      type="text"
                      value={learnerClass}
                      onChange={(e) => setLearnerClass(e.target.value)}
                      placeholder="Leave blank for fill-in space"
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Layout */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Page Layout
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setLayout('portrait')}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition ${
                        layout === 'portrait'
                          ? 'border-indigo-500 bg-indigo-500/20 text-indigo-300'
                          : 'border-slate-600 text-slate-400 hover:border-slate-500'
                      }`}
                    >
                      <AlignLeft className="h-5 w-5" />
                      <div className="text-left">
                        <div className="font-medium">Portrait</div>
                        <div className="text-xs opacity-70">Single column</div>
                      </div>
                    </button>
                    <button
                      onClick={() => setLayout('landscape')}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition ${
                        layout === 'landscape'
                          ? 'border-indigo-500 bg-indigo-500/20 text-indigo-300'
                          : 'border-slate-600 text-slate-400 hover:border-slate-500'
                      }`}
                    >
                      <Columns className="h-5 w-5" />
                      <div className="text-left">
                        <div className="font-medium">Landscape</div>
                        <div className="text-xs opacity-70">Two columns</div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Signature & Watermark */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      <Stamp className="h-4 w-4 inline mr-2" />
                      Footer Signature
                    </label>
                    <input
                      type="text"
                      value={signature}
                      onChange={(e) => setSignature(e.target.value)}
                      placeholder="e.g. © 2025 Your School"
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      <Droplets className="h-4 w-4 inline mr-2" />
                      Watermark Text
                    </label>
                    <input
                      type="text"
                      value={watermark}
                      onChange={(e) => setWatermark(e.target.value)}
                      placeholder="e.g. DRAFT, CONFIDENTIAL"
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Filters Section */}
          <div className="border border-slate-700 rounded-lg overflow-hidden">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-between w-full px-4 py-3 bg-slate-800/50 hover:bg-slate-800 transition"
            >
              <span className="flex items-center gap-2 text-sm font-medium text-slate-200">
                <Search className="h-4 w-4 text-indigo-400" />
                Filter Questions
                {hasActiveFilters && (
                  <span className="px-2 py-0.5 bg-indigo-600 text-white rounded-full text-xs">
                    Active
                  </span>
                )}
              </span>
              {showFilters ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
            </button>
            
            {showFilters && (
              <div className="p-4 bg-slate-800/30 space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search question text..."
                    className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <CategoryFilter
                  categories={categories}
                  selectedFilters={filters}
                  onFilterChange={setFilters}
                  onClear={clearFilters}
                  questions={questions}
                />
              </div>
            )}
          </div>

          {/* Questions List */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-slate-300">
                {filteredQuestions.length} questions found
              </span>
              <button
                onClick={toggleAll}
                className="flex items-center gap-1 text-sm text-indigo-400 hover:text-indigo-300"
              >
                {selectedQuestionIds.size === filteredQuestions.length ? (
                  <>
                    <CheckSquare className="h-4 w-4" />
                    Deselect All
                  </>
                ) : (
                  <>
                    <Square className="h-4 w-4" />
                    Select All
                  </>
                )}
              </button>
            </div>

            <div className="border border-slate-700 rounded-lg divide-y divide-slate-700 max-h-64 overflow-y-auto bg-slate-800/50">
              {filteredQuestions.length === 0 ? (
                <div className="p-4 text-center text-slate-500 text-sm">
                  No questions match the selected filters
                </div>
              ) : (
                filteredQuestions.map((q) => {
                  const orderNum = getOrderNumber(q.id);
                  const isSelected = selectedQuestionIds.has(q.id);
                  
                  return (
                    <div key={q.id} className={`p-3 transition ${isSelected ? 'bg-slate-800/80' : 'hover:bg-slate-800/50'}`}>
                      <div className="flex items-start gap-2">
                        {/* Order number & controls */}
                        {isSelected && (
                          <div className="flex flex-col items-center gap-0.5 shrink-0">
                            <button
                              onClick={() => moveQuestion(q.id, 'up')}
                              disabled={orderedSelectedIds.indexOf(q.id) === 0}
                              className="p-0.5 text-slate-500 hover:text-indigo-400 disabled:opacity-30 disabled:hover:text-slate-500"
                            >
                              <ArrowUp className="h-3.5 w-3.5" />
                            </button>
                            <span className="text-xs font-bold text-indigo-400 min-w-[20px] text-center">
                              {orderNum}
                            </span>
                            <button
                              onClick={() => moveQuestion(q.id, 'down')}
                              disabled={orderedSelectedIds.indexOf(q.id) === orderedSelectedIds.length - 1}
                              className="p-0.5 text-slate-500 hover:text-indigo-400 disabled:opacity-30 disabled:hover:text-slate-500"
                            >
                              <ArrowDown className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                        
                        {/* Checkbox */}
                        <button
                          onClick={() => toggleQuestion(q.id)}
                          className="mt-1 shrink-0"
                        >
                          {isSelected ? (
                            <CheckSquare className="h-5 w-5 text-indigo-500" />
                          ) : (
                            <Square className="h-5 w-5 text-slate-500" />
                          )}
                        </button>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            {q.type === 'stem' && (
                              <span className="px-1.5 py-0.5 bg-purple-600/30 text-purple-400 rounded text-xs font-medium">
                                STEM ({q.subQuestions?.length || 0})
                              </span>
                            )}
                            {q.subject && (
                              <span className="text-xs text-slate-500">{q.subject}</span>
                            )}
                            {getQuestionGrades(q) && (
                              <span className="text-xs text-slate-500">
                                • {getQuestionGrades(q)}
                              </span>
                            )}
                            {q.level && (
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                q.level === 1 ? 'bg-green-600/30 text-green-400' :
                                q.level === 2 ? 'bg-yellow-600/30 text-yellow-400' :
                                q.level === 3 ? 'bg-orange-600/30 text-orange-400' :
                                'bg-red-600/30 text-red-400'
                              }`}>
                                L{q.level}
                              </span>
                            )}
                            {q.marks > 1 && (
                              <span className="px-1.5 py-0.5 bg-amber-600/30 text-amber-400 rounded text-xs flex items-center gap-0.5">
                                <Award size={10} />
                                {q.marks}
                              </span>
                            )}
                          </div>
                          
                          {q.topics?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-1">
                              {q.topics.slice(0, 3).map(topic => (
                                <span key={topic} className="px-1.5 py-0.5 bg-green-600/20 text-green-400 rounded text-xs">
                                  {topic}
                                </span>
                              ))}
                              {q.topics.length > 3 && (
                                <span className="text-xs text-slate-500">+{q.topics.length - 3}</span>
                              )}
                            </div>
                          )}
                          
                          <div 
                            className="text-sm text-slate-300 cursor-pointer"
                            onClick={() => toggleExpanded(q.id)}
                          >
                            {expandedQuestions.has(q.id) ? (
                              <div className="prose prose-sm prose-invert max-w-none">
                                <MathRenderer content={q.type === 'simple' ? q.text : q.stemText} />
                              </div>
                            ) : (
                              <span className="line-clamp-2">{getQuestionPreview(q)}</span>
                            )}
                          </div>
                          
                          <button
                            onClick={() => toggleExpanded(q.id)}
                            className="text-xs text-indigo-400 hover:text-indigo-300 mt-1"
                          >
                            {expandedQuestions.has(q.id) ? 'Show less' : 'Show more'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Options */}
          <div>
            <label className="flex items-center gap-3 p-3 bg-slate-800/50 border border-slate-700 rounded-lg cursor-pointer hover:bg-slate-800 transition">
              <input
                type="checkbox"
                checked={showAnswers}
                onChange={(e) => setShowAnswers(e.target.checked)}
                disabled={isExporting}
                className="w-5 h-5 rounded bg-slate-700 border-slate-600 text-indigo-600 focus:ring-indigo-500"
              />
              <div>
                <span className="font-medium text-slate-200">Include Answer Key</span>
                <p className="text-sm text-slate-500">Highlight correct answers with green circles</p>
              </div>
            </label>
          </div>

          <div>
            <label className="flex items-center gap-3 p-3 bg-slate-800/50 border border-slate-700 rounded-lg cursor-pointer hover:bg-slate-800 transition">
              <input
                type="checkbox"
                checked={showResponseSection}
                onChange={(e) => setShowResponseSection(e.target.checked)}
                disabled={isExporting}
                className="w-5 h-5 rounded bg-slate-700 border-slate-600 text-indigo-600 focus:ring-indigo-500"
              />
              <div>
                <span className="font-medium text-slate-200">Include Response Section</span>
                <p className="text-sm text-slate-500">Add answer lines, working boxes & paragraph areas</p>
              </div>
            </label>
          </div>

          {/* Server info */}
          <div className="flex items-center gap-2 text-sm text-indigo-400">
            <Server className="h-4 w-4" />
            <span>Server-side rendering for perfect math</span>
          </div>

          {error && (
            <div className="p-3 bg-red-900/30 border border-red-600 rounded-lg">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {isExporting && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Loader2 className="h-4 w-4 animate-spin text-indigo-400" />
                <span className="text-sm text-slate-400">{status}</span>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-1.5 overflow-hidden">
                <div className="bg-indigo-500 h-1.5 rounded-full animate-[loading_2s_ease-in-out_infinite]" 
                     style={{ width: '30%' }} />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-700 bg-slate-800/50 rounded-b-xl shrink-0">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-400">
              <span className="font-medium text-indigo-400">{selectedQuestionIds.size}</span> of {filteredQuestions.length} questions selected
            </p>
            <button
              onClick={handleExport}
              disabled={isExporting || selectedQuestionIds.size === 0}
              className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-500 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isExporting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="h-5 w-5" />
                  Download PDF
                </>
              )}
            </button>
          </div>
        </div>

        <style>{`
          @keyframes loading {
            0% { transform: translateX(-100%); }
            50% { transform: translateX(200%); }
            100% { transform: translateX(-100%); }
          }
        `}</style>
      </div>
    </div>
  );
};

export default PdfExportModal;